import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Database, Plus, Eye, Edit, Trash2 } from 'lucide-react'
import { api } from '../lib/api'
import AddCollectionModal from '../components/AddCollectionModal'
import ViewCollectionModal from '../components/ViewCollectionModal'
import EditCollectionModal from '../components/EditCollectionModal'

export default function CollectionsPage() {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [viewingCollection, setViewingCollection] = useState<any>(null)
  const [editingCollection, setEditingCollection] = useState<any>(null)
  const queryClient = useQueryClient()

  const { data: collections, isLoading } = useQuery({
    queryKey: ['collections', 'stats'],
    queryFn: () => api.getCollections(true),
  })

  const deleteCollectionMutation = useMutation({
    mutationFn: (collectionId: string) => api.deleteCollection(collectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collections'] })
    }
  })

  const handleDeleteCollection = async (collectionId: string, title: string) => {
    if (window.confirm(`Are you sure you want to delete collection "${title}"?`)) {
      deleteCollectionMutation.mutate(collectionId)
    }
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Collections</h1>
          <p className="text-gray-600">
            Manage TAXII collections for organizing threat intelligence
          </p>
        </div>
        <button 
          onClick={() => setIsAddModalOpen(true)}
          className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 flex items-center space-x-2"
        >
          <Plus className="h-4 w-4" />
          <span>New Collection</span>
        </button>
      </div>

      {collections?.collections && collections.collections.length > 0 ? (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {collections.collections.map((collection: any) => (
              <li key={collection.id}>
                <div className="px-4 py-4 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="bg-blue-100 rounded-md p-2">
                      <Database className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        {collection.title}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {collection.description || 'No description'}
                      </p>
                      <div className="flex items-center space-x-4 mt-1">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          collection.can_read ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {collection.can_read ? 'Readable' : 'No Read'}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          collection.can_write ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {collection.can_write ? 'Writable' : 'Read-only'}
                        </span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {collection.object_count || 0} objects
                        </span>
                      </div>
                      {collection.last_modified && (
                        <p className="text-xs text-gray-400 mt-1">
                          Last modified: {new Date(collection.last_modified).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button 
                      onClick={() => setViewingCollection(collection)}
                      className="text-gray-400 hover:text-blue-600"
                      title="View collection details"
                    >
                      <Eye className="h-5 w-5" />
                    </button>
                    <button 
                      onClick={() => setEditingCollection(collection)}
                      className="text-gray-400 hover:text-gray-600"
                      title="Edit collection"
                    >
                      <Edit className="h-5 w-5" />
                    </button>
                    <button 
                      onClick={() => handleDeleteCollection(collection.id, collection.title)}
                      disabled={deleteCollectionMutation.isPending}
                      className="text-gray-400 hover:text-red-600 disabled:opacity-50"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg p-12 text-center">
          <Database className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No collections found</h3>
          <p className="text-gray-500 mb-6">
            Create your first TAXII collection to start organizing threat intelligence data.
          </p>
          <button 
            onClick={() => setIsAddModalOpen(true)}
            className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700"
          >
            Create Collection
          </button>
        </div>
      )}

      <AddCollectionModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
      />

      {viewingCollection && (
        <ViewCollectionModal
          isOpen={!!viewingCollection}
          onClose={() => setViewingCollection(null)}
          collection={viewingCollection}
        />
      )}

      {editingCollection && (
        <EditCollectionModal
          isOpen={!!editingCollection}
          onClose={() => setEditingCollection(null)}
          collection={editingCollection}
        />
      )}
    </div>
  )
}